import React, { useState, useRef, useCallback, useEffect } from 'react';
import Header from '../../components/Header/Header';
import Button from '../../components/Button/Button';
import icon from '../../assets/sidebar/uploadWhite.svg';
import uploadStock from '../../api/cars/uploadStock';
import clearStock from '../../api/cars/clearStock';
import alert from '../../helpers/alert';
import isFileSizeSmallerThan from '../../utils/validateFileSize';
import isUserHaveRole from '../../utils/isUserHaveRole';
import { ROLE_ADMIN } from '../../constants/roles';
import StockPendingRowsTable from './StockPendingRowsTable';
import BitrixImportPanel from './BitrixImportPanel';
import StockImportHistory from './StockImportHistory';
import { importCounters, addImportCounters } from './importCounters';
import StockImportNotices from './StockImportNotices';
import styles from './stockUpload.module.css';

const ALLOWED_EXTENSIONS = ['.xlsx', '.xls', '.csv'];
const MAX_FILE_SIZE_MB = 30;
const MAX_FILES = 15;

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const getFileExtension = (filename) => {
  const lastDot = filename.lastIndexOf('.');
  return lastDot !== -1 ? filename.substring(lastDot).toLowerCase() : '';
};

const StockUpload = () => {
  // Вся пачка выбранных файлов уходит одним запросом и обрабатывается бэком как
  // один логический импорт (см. uploadStock.js) — поэтому тут именно список, а
  // не последовательная отправка по одному файлу
  const [files, setFiles] = useState([]);
  // Источник строк — файлы или Битрикс. Всё, что после получения результата
  // (карточка итогов, ручная правка, коммит), у обоих источников общее
  const [source, setSource] = useState('file');
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  // Очистка всего стока — только админ. Двухшаговое подтверждение вместо
  // системного confirm: первый клик переводит кнопку в состояние "Подтвердить",
  // второй — выполняет. Состояние самосбрасывается через таймаут, чтобы
  // подтверждение не сработало случайно спустя время
  const isAdmin = isUserHaveRole(ROLE_ADMIN);
  const [clearConfirm, setClearConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    if (!clearConfirm) return undefined;
    const timer = setTimeout(() => setClearConfirm(false), 5000);
    return () => clearTimeout(timer);
  }, [clearConfirm]);

  const handleClearStock = async () => {
    if (!clearConfirm) {
      setClearConfirm(true);
      return;
    }
    setClearing(true);
    try {
      const res = await clearStock();
      alert.success(`Сток очищен: снято с публикации машин — ${res?.data?.cleared ?? 0}`);
      setResult(null);
    } catch (err) {
      alert.error(err?.response?.data?.errorMessage || 'Ошибка при очистке стока. Попробуйте еще раз.');
    } finally {
      setClearing(false);
      setClearConfirm(false);
    }
  };

  const validateFile = useCallback((selectedFile) => {
    const ext = getFileExtension(selectedFile.name);
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setValidationError(
        `Файл «${selectedFile.name}»: недопустимый формат. Загрузите файл в формате .xlsx, .xls или .csv`
      );
      return false;
    }

    if (!isFileSizeSmallerThan(selectedFile, MAX_FILE_SIZE_MB)) {
      setValidationError(
        `Файл «${selectedFile.name}» превышает максимально допустимый размер (${MAX_FILE_SIZE_MB} МБ)`
      );
      return false;
    }

    return true;
  }, []);

  const handleFiles = useCallback(
    (selectedFiles) => {
      setValidationError('');
      setResult(null);

      const incoming = Array.from(selectedFiles || []);
      if (incoming.length === 0) return;

      const valid = incoming.filter(validateFile);
      if (valid.length === 0) return;

      setFiles((prev) => {
        // Дедуп по имени+размеру: один и тот же файл можно случайно перетащить
        // дважды — грузить его в пачке два раза бессмысленно (дубли VIN уйдут
        // в ошибки)
        const seen = new Set(prev.map((f) => `${f.name}|${f.size}`));
        const merged = [...prev];
        valid.forEach((f) => {
          const key = `${f.name}|${f.size}`;
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(f);
          }
        });
        if (merged.length > MAX_FILES) {
          setValidationError(`Можно загрузить не более ${MAX_FILES} файлов за раз`);
          return merged.slice(0, MAX_FILES);
        }
        return merged;
      });
    },
    [validateFile]
  );

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [handleFiles]
  );

  const handleInputChange = useCallback(
    (e) => {
      if (e.target.files && e.target.files.length > 0) {
        handleFiles(e.target.files);
      }
      // Сбрасываем input, чтобы удалённый из списка файл можно было выбрать снова
      e.target.value = '';
    },
    [handleFiles]
  );

  const handleBrowseClick = () => {
    inputRef.current?.click();
  };

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setValidationError('');
  };

  const handleClearFiles = () => {
    setFiles([]);
    setValidationError('');
    setResult(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setUploading(true);
    setValidationError('');
    setResult(null);

    try {
      const response = await uploadStock(files);
      const data = response.data;

      if (data.success) {
        setResult({
          type: 'success',
          totalRows: data.totalRows,
          imported: data.imported,
          ...importCounters(data),
          files: data.files || [],
        });
        alert.success('Сток автомобилей успешно загружен');
        setFiles([]);
        if (inputRef.current) {
          inputRef.current.value = '';
        }
      } else {
        setResult({
          type: 'error',
          totalRows: data.totalRows,
          imported: data.imported,
          ...importCounters(data),
          errors: data.errors || [],
          files: data.files || [],
          sessionId: data.sessionId || null,
          pendingRows: data.pendingRows || [],
        });
        alert.error('Загрузка завершена с ошибками');
        setFiles([]);
        if (inputRef.current) {
          inputRef.current.value = '';
        }
      }
    } catch (err) {
      const message =
        err?.response?.data?.errorMessage ||
        err?.response?.data?.message ||
        'Ошибка при загрузке файла. Попробуйте еще раз.';
      setResult({
        type: 'error',
        totalRows: 0,
        imported: 0,
        errors: [{ row: '—', message }],
        files: [],
      });
      alert.error(message);
      setFiles([]);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    } finally {
      setUploading(false);
    }
  };

  // Импорт из Битрикса возвращает тот же StockImportResult, что и загрузка файлов,
  // поэтому дальше всё рисуется теми же карточками и той же таблицей правки
  const handleBitrixResult = (data) => {
    if (!data) return;
    setResult({
      type: data.success ? 'success' : 'error',
      totalRows: data.totalRows,
      imported: data.imported,
      ...importCounters(data),
      errors: data.errors || [],
      files: data.files || [],
      sessionId: data.sessionId || null,
      pendingRows: data.pendingRows || [],
    });
  };

  const handleSourceChange = (next) => {
    setSource(next);
    setResult(null);
    setValidationError('');
  };

  const handlePendingRowsResolved = (data) => {
    setResult((prev) => {
      if (!prev) return prev;
      const pendingRows = data.pendingRows || [];
      // errors/type должны отражать ТЕКУЩЕЕ состояние (что реально ещё не
      // решено), а не застывший снимок из самой первой загрузки — иначе после
      // того как все строки поправлены и импортированы, тут продолжал бы
      // висеть исходный список ошибок и красный баннер "завершено с ошибками",
      // хотя на деле всё уже успешно загружено (см. imported === totalRows)
      const errors = pendingRows.map((r) => ({
        row: r.row,
        fileName: r.fileName,
        message: r.errors?.[0] || 'Требуется ручная проверка',
      }));
      return {
        ...prev,
        imported: prev.imported + (data.imported || 0),
        ...addImportCounters(prev, data),
        sessionId: data.sessionId || null,
        pendingRows,
        errors,
        type: pendingRows.length > 0 ? 'error' : 'success',
      };
    });
  };

  return (
    <>
      <Header title="Загрузка стока" icon={icon} />
      <div className={styles.container}>
        {uploading && (
          <div className={styles.overlay}>
            <div className={styles.overlaySpinner} />
            <div className={styles.overlayText}>Загрузка файлов...</div>
          </div>
        )}

        <div className={styles.sourceTabs}>
          <button
            type="button"
            className={`${styles.sourceTab} ${source === 'file' ? styles.sourceTabActive : ''}`}
            onClick={() => handleSourceChange('file')}
            disabled={uploading}
          >
            Файлы Excel
          </button>
          <button
            type="button"
            className={`${styles.sourceTab} ${source === 'bitrix' ? styles.sourceTabActive : ''}`}
            onClick={() => handleSourceChange('bitrix')}
            disabled={uploading}
          >
            Битрикс
          </button>
        </div>

        {source === 'bitrix' && <BitrixImportPanel onResult={handleBitrixResult} />}

        {source === 'file' && (
          <>
        <div
          className={`${styles.dropZone} ${dragActive ? styles.dropZoneActive : ''}`}
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDrop}
          onClick={handleBrowseClick}
        >
          <div className={styles.dropZoneIcon}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"/>
            </svg>
          </div>
          <div className={styles.dropZoneTitle}>
            Перетащите файлы сюда или нажмите для выбора
          </div>
          <div className={styles.dropZoneSubtitle}>
            Поддерживаемые форматы: .xlsx, .xls, .csv. Можно выбрать несколько файлов — они
            будут обработаны как одна загрузка
          </div>
          <div className={styles.dropZoneHint}>
            До {MAX_FILES} файлов, максимальный размер файла: {MAX_FILE_SIZE_MB} МБ
          </div>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          multiple
          className={styles.fileInput}
          onChange={handleInputChange}
        />

        {files.length > 0 && (
          <div className={styles.fileList}>
            {files.map((file, index) => (
              <div key={`${file.name}|${file.size}`} className={styles.fileInfo}>
                <div className={styles.fileInfoIcon}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zM6 20V4h7v5h5v11H6z"/>
                  </svg>
                </div>
                <div className={styles.fileInfoDetails}>
                  <div className={styles.fileName}>{file.name}</div>
                  <div className={styles.fileSize}>{formatFileSize(file.size)}</div>
                </div>
                <button
                  type="button"
                  className={styles.fileRemove}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFile(index);
                  }}
                  disabled={uploading}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}

        <div className={styles.actions}>
          <Button
            fill
            text={files.length > 1 ? `Загрузить (${files.length})` : 'Загрузить'}
            onClick={handleUpload}
            disabled={files.length === 0 || uploading}
          />
          {files.length > 0 && !uploading && (
            <Button text="Отмена" onClick={handleClearFiles} />
          )}
          {isAdmin && (
            <Button
              text={clearing ? 'Очистка…' : clearConfirm ? 'Подтвердить очистку стока?' : 'Очистить сток'}
              onClick={handleClearStock}
              disabled={uploading || clearing}
              style={clearConfirm ? { backgroundColor: '#d9534f', color: '#fff', borderColor: '#d9534f' } : {}}
            />
          )}
        </div>

        {validationError && <div className={styles.error}>{validationError}</div>}
          </>
        )}

        {result && result.type === 'success' && (
          <div className={styles.resultCard}>
            <div className={styles.resultHeaderSuccess}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M10 20C4.477 20 0 15.523 0 10C0 4.477 4.477 0 10 0C15.523 0 20 4.477 20 10C20 15.523 15.523 20 10 20ZM10 18C12.1217 18 14.1566 17.1571 15.6569 15.6569C17.1571 14.1566 18 12.1217 18 10C18 7.87827 17.1571 5.84344 15.6569 4.34315C14.1566 2.84285 12.1217 2 10 2C7.87827 2 5.84344 2.84285 4.34315 4.34315C2.84285 5.84344 2 7.87827 2 10C2 12.1217 2.84285 14.1566 4.34315 15.6569C5.84344 17.1571 7.87827 18 10 18ZM9.003 14L4.76 9.757L6.174 8.343L9.003 11.172L14.659 5.515L16.074 6.929L9.003 14Z"
                  fill="#5cb85c"
                />
              </svg>
              <span>Импорт завершен успешно</span>
            </div>
            <div className={styles.resultStats}>
              <div className={styles.resultStat}>
                <span className={styles.resultStatLabel}>Всего строк:</span>
                <span className={styles.resultStatValue}>{result.totalRows}</span>
              </div>
              <div className={styles.resultStat}>
                <span className={styles.resultStatLabel}>Импортировано:</span>
                <span className={styles.resultStatValue + ' ' + styles.resultStatSuccess}>{result.imported}</span>
              </div>
              {result.imported > 0 && (
                <div className={styles.resultStat}>
                  <span className={styles.resultStatLabel}>Цена по приказу:</span>
                  <span className={styles.resultStatValue}>{result.pricedByOrder ?? 0}</span>
                </div>
              )}
              {result.noPrice > 0 && (
                <div className={styles.resultStat}>
                  <span className={styles.resultStatLabel}>Без цены:</span>
                  <span className={styles.resultStatValue}>{result.noPrice}</span>
                </div>
              )}
            </div>
            <StockImportNotices result={result} />
            {result.files?.length > 1 && (
              <div className={styles.fileStats}>
                {result.files.map((f, idx) => (
                  <div key={idx} className={styles.fileStatRow}>
                    <span className={styles.fileStatName}>{f.fileName}</span>
                    <span className={styles.fileStatNumbers}>
                      строк: {f.totalRows} · импортировано: {f.imported} · ошибок: {f.errorCount}
                      {f.noPriceRule > 0 && ` · без правила: ${f.noPriceRule}`}
                      {f.noPrice > 0 && ` · без цены: ${f.noPrice}`}
                      {f.noEngineVolume > 0 && ` · без объёма: ${f.noEngineVolume}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {result && result.type === 'error' && (
          <div className={styles.resultCard}>
            <div className={styles.resultHeaderError}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M10 20C4.477 20 0 15.523 0 10C0 4.477 4.477 0 10 0C15.523 0 20 4.477 20 10C20 15.523 15.523 20 10 20ZM10 18C12.1217 18 14.1566 17.1571 15.6569 15.6569C17.1571 14.1566 18 12.1217 18 10C18 7.87827 17.1571 5.84344 15.6569 4.34315C14.1566 2.84285 12.1217 2 10 2C7.87827 2 5.84344 2.84285 4.34315 4.34315C2.84285 5.84344 2 7.87827 2 10C2 12.1217 2.84285 14.1566 4.34315 15.6569C5.84344 17.1571 7.87827 18 10 18ZM9 13H11V15H9V13ZM9 5H11V11H9V5Z"
                  fill="#d9534f"
                />
              </svg>
              <span>Загрузка завершена с ошибками</span>
            </div>
            <div className={styles.resultStats}>
              <div className={styles.resultStat}>
                <span className={styles.resultStatLabel}>Всего строк:</span>
                <span className={styles.resultStatValue}>{result.totalRows}</span>
              </div>
              <div className={styles.resultStat}>
                <span className={styles.resultStatLabel}>Импортировано:</span>
                <span className={styles.resultStatValue + ' ' + styles.resultStatSuccess}>{result.imported}</span>
              </div>
              {result.imported > 0 && (
                <div className={styles.resultStat}>
                  <span className={styles.resultStatLabel}>Цена по приказу:</span>
                  <span className={styles.resultStatValue}>{result.pricedByOrder ?? 0}</span>
                </div>
              )}
              {result.noPrice > 0 && (
                <div className={styles.resultStat}>
                  <span className={styles.resultStatLabel}>Без цены:</span>
                  <span className={styles.resultStatValue}>{result.noPrice}</span>
                </div>
              )}
              <div className={styles.resultStat}>
                <span className={styles.resultStatLabel}>Ошибок:</span>
                <span className={styles.resultStatValue + ' ' + styles.resultStatError}>{result.errors.length}</span>
              </div>
            </div>
            <StockImportNotices result={result} />
            {result.files?.length > 1 && (
              <div className={styles.fileStats}>
                {result.files.map((f, idx) => (
                  <div key={idx} className={styles.fileStatRow}>
                    <span className={styles.fileStatName}>{f.fileName}</span>
                    <span className={styles.fileStatNumbers}>
                      строк: {f.totalRows} · импортировано: {f.imported} · ошибок: {f.errorCount}
                      {f.noPriceRule > 0 && ` · без правила: ${f.noPriceRule}`}
                      {f.noPrice > 0 && ` · без цены: ${f.noPrice}`}
                      {f.noEngineVolume > 0 && ` · без объёма: ${f.noEngineVolume}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {/* Когда есть сессия правки — те же ошибки уже показаны по строкам ниже
                (компактным списком «без происхождения» и формами для реальных
                ошибок), плоский список тут только дублирует и раздувает страницу
                на больших файлах. Показываем его только для фатальных ошибок без
                сессии (например файл вообще не распарсился) */}
            {result.errors.length > 0 && !result.sessionId && (
              <div className={styles.errorTable}>
                <div className={styles.errorTableHeader}>
                  <span className={styles.errorTableColRow}>Строка</span>
                  <span className={styles.errorTableColMsg}>Ошибка</span>
                </div>
                {result.errors.map((err, idx) => (
                  <div key={idx} className={styles.errorTableRow}>
                    {/* row = 0 у ошибок уровня источника (нечитаемый файл, непонятый
                        ДЦ) — номера строки у них нет, и печатать ноль незачем */}
                    <span className={styles.errorTableColRow}>{err.row || '—'}</span>
                    <span className={styles.errorTableColMsg}>
                      {err.fileName ? `[${err.fileName}] ` : ''}
                      {err.message}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {result?.sessionId && result.pendingRows?.length > 0 && (
          <StockPendingRowsTable
            key={result.sessionId}
            sessionId={result.sessionId}
            pendingRows={result.pendingRows}
            onResolved={handlePendingRowsResolved}
          />
        )}

        {/* Отчёты прошлых прогонов: раньше результат жил только до закрытия вкладки */}
        <StockImportHistory />
      </div>
    </>
  );
};

export default StockUpload;
